export type HistoricalImportRow = Record<string, string> & {
  entity_kind: "client" | "property_type" | "property" | "service" | "worker" | "mission_record" | "mission_service_line" | "mission_report" | "mission_consumption" | "financial_document" | "financial_payment" | "inventory_item" | "inventory_movement";
  source_key: string;
  name: string;
};

const numericColumns = new Set([
  "unit_price_ex_vat", "vat_rate", "default_duration_hours", "payment_terms_days",
  "monthly_hours", "daily_hours", "net_salary", "employer_cost_monthly", "ticket_count",
  "ticket_value", "hourly_rate", "fixed_fee", "monthly_fee", "actual_hours",
  "revenue_ex_vat", "labor_cost_ex_vat", "material_cost_ex_vat", "equipment_cost_ex_vat",
  "travel_cost_ex_vat", "other_cost_ex_vat", "total_cost_ex_vat", "quantity",
  "unit_cost_ex_vat", "total_ex_vat", "total_vat", "total_inc_vat",
  "declared_amount_paid", "amount", "minimum_quantity", "sale_price_ex_vat",
  "sale_vat_rate", "unit_cost", "line_total_ex_vat", "planned_hours",
  "direct_cost_ex_vat", "position",
]);

function normalizeImportedNumber(header: string, rawValue: string) {
  const trimmed = rawValue.trim();
  if (!trimmed || !numericColumns.has(header)) return trimmed;

  let normalized = trimmed
    .replace(/[\u00A0\u202F\s]/g, "")
    .replace(/(?:euros?|eur|€)$/i, "");
  if (header.includes("hours")) normalized = normalized.replace(/(?:heures?|hrs?|h)$/i, "");
  if (header.includes("rate")) normalized = normalized.replace(/%$/, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.lastIndexOf(",") > normalized.lastIndexOf(".")
      ? normalized.replaceAll(".", "").replace(",", ".")
      : normalized.replaceAll(",", "");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  return /^[+-]?\d+(?:\.\d+)?$/.test(normalized) ? normalized : trimmed;
}

function countOutsideQuotes(value: string, searched: string) {
  let count = 0;
  let quoted = false;
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === '"') {
      if (quoted && value[index + 1] === '"') index += 1;
      else quoted = !quoted;
    } else if (!quoted && value[index] === searched) count += 1;
  }
  return count;
}

function detectDelimiter(content: string) {
  const firstLine = content.split(/\r?\n/, 1)[0] || "";
  return countOutsideQuotes(firstLine, ";") >= countOutsideQuotes(firstLine, ",") ? ";" : ",";
}

function parseRecords(content: string, delimiter: string) {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;

  const pushCell = () => {
    record.push(cell.trim());
    cell = "";
  };
  const pushRecord = () => {
    pushCell();
    if (record.some((value) => value.length > 0)) records.push(record);
    record = [];
  };

  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];
    if (character === '"') {
      if (quoted && content[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (character === delimiter && !quoted) {
      pushCell();
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && content[index + 1] === "\n") index += 1;
      pushRecord();
    } else {
      cell += character;
    }
  }

  if (quoted) throw new Error("Le fichier contient un guillemet non fermé.");
  if (cell.length > 0 || record.length > 0) pushRecord();
  return records;
}

export function parseHistoricalImportCsv(rawContent: string): HistoricalImportRow[] {
  const content = rawContent.replace(/^\uFEFF/, "");
  const records = parseRecords(content, detectDelimiter(content));
  if (records.length < 2) throw new Error("Le fichier doit contenir un en-tête et au moins une ligne.");

  const headers = records[0].map((value) => value.trim().toLowerCase());
  const namedHeaders = headers.filter(Boolean);
  if (new Set(namedHeaders).size !== namedHeaders.length) {
    throw new Error("Le fichier contient une colonne en double.");
  }
  for (const required of ["entity_kind", "source_key", "name"]) {
    if (!headers.includes(required)) throw new Error(`Colonne obligatoire absente : ${required}.`);
  }

  return records.slice(1).map((values, rowIndex) => {
    if (values.length > headers.length && values.slice(headers.length).some(Boolean)) {
      throw new Error(`La ligne ${rowIndex + 2} contient plus de valeurs que l'en-tête.`);
    }
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      if (header) row[header] = normalizeImportedNumber(header, values[index] || "");
    });
    return row as HistoricalImportRow;
  });
}
